'use client';

import { forwardRef } from 'react';
import type { MouseEventHandler } from 'react';
import { createPolymorphicComponent, Button as MantineButton } from '@mantine/core';
import type { ButtonProps as MantineButtonProps } from '@mantine/core';

import type { LockedPolymorphic, LockedProps } from '../types';

export type { ButtonVariant } from '@mantine/core';

export type ButtonProps = Omit<MantineButtonProps, LockedProps> & {
  // Not on MantineButtonProps (it's polymorphic — these come from the rendered element), but we
  // read them here to block activation and drop the element from the tab order while disabled.
  onClick?: MouseEventHandler;
  tabIndex?: number;
  'aria-disabled'?: boolean;
};

const _Button = forwardRef<HTMLButtonElement, ButtonProps>((props, ref) => {
  const { disabled, loading, onClick, tabIndex, 'aria-disabled': ariaDisabled, ...rest } = props;

  // When rendered as a polymorphic link (`component={Link}` / `component="a"`), the browser
  // ignores the `disabled` attribute on the anchor, so a "disabled" button stays clickable,
  // navigable, and in the tab order. Guard the click to actually block activation and drop it
  // from the tab order — matching how a native disabled <button> behaves (for which both are a
  // harmless no-op). `aria-disabled` carries the state to assistive tech on anchors, where the
  // `disabled` attribute is invalid.
  const isDisabled = disabled || loading;
  const handleClick: MouseEventHandler = (event) => {
    if (isDisabled) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    onClick?.(event);
  };

  return (
    <MantineButton
      ref={ref}
      {...rest}
      // A caller's own `aria-disabled` is kept, for a button that stays hoverable (to show a
      // tooltip saying why) yet is announced as disabled.
      aria-disabled={isDisabled || ariaDisabled || undefined}
      disabled={disabled}
      loading={loading}
      onClick={handleClick}
      tabIndex={isDisabled ? -1 : tabIndex}
    />
  );
});

_Button.displayName = 'Button';

/**
 * ### Button
 *
 * @description
 * Mantine's Button minus per-call-site style overrides (see {@link LockedProps}). Style with
 * Mantine props and variants (`variant`, `color`, `size`, …) — there are no bespoke aliases.
 * `className`/`style` are locked even though the component is polymorphic: the intrinsic element
 * props would otherwise re-widen them back in.
 *
 * Keeps one behavior: when rendered as an anchor (`component={Link}` / `component="a"`) while
 * `disabled`/`loading`, it blocks activation, drops the element from the tab order, and sets
 * `aria-disabled` — since the native `disabled` attribute does nothing on an anchor.
 *
 * @example
 * ```tsx
 * <Button>Primary</Button>
 * <Button variant="outline">Outline</Button>
 * <Button variant="default">Cancel</Button>
 * <Button component={Link} href="/next" disabled>Continue</Button>
 * ```
 */
export const Button = createPolymorphicComponent<'button', ButtonProps>(
  _Button,
) as LockedPolymorphic<'button', ButtonProps>;
